"use client";

import { BrandMark } from "@/components/brand";
import { sessionCopy } from "@/shared/lesson/view-contracts";
import type { Room } from "livekit-client";
import { Check, Maximize, PanelsTopLeft, Video, VideoOff, X } from "lucide-react";
import { type ReactNode, type RefObject, useEffect, useId, useRef, useState } from "react";
import { SessionMicButton } from "../SessionMicButton";
import styles from "./TutorStage.module.css";
import { TUTOR_VIEWS, type TutorView } from "./tutor-view";

const CONVERSATION_OPTIONS = [
  { latestOnly: true, label: "Latest message" },
  { latestOnly: false, label: "Full conversation" },
];

export interface TutorTranslationOptions {
  enabled: boolean;
  languageCode: string;
  languageOptions: Array<{ code: string; name: string }>;
  onEnabledChange: (enabled: boolean) => void;
  onLanguageChange: (languageCode: string) => void;
}

/**
 * One "View options" menu: where the avatar appears, how much of the
 * conversation shows, and translations (on desktop they live here rather than
 * in a bar of their own).
 */
export function TutorViewMenu({
  view,
  onViewChange,
  onFullscreen,
  fullscreenAvailable,
  conversation,
  translations,
}: {
  view: TutorView;
  onViewChange: (view: TutorView) => void;
  onFullscreen: (trigger: HTMLElement | null) => void;
  /** False only when the avatar video failed; with the avatar off it shows her. */
  fullscreenAvailable: boolean;
  /** Compact and Large only: the latest message alone, or the full conversation. */
  conversation?: { latestOnly: boolean; onChange: (latestOnly: boolean) => void };
  translations?: TutorTranslationOptions;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // The menu takes this Escape; the session's own Escape (end session)
      // must not also fire.
      event.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={styles.viewMenu}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.stageIconButton}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label="View options"
        title="View options"
        onClick={() => setOpen((value) => !value)}
      >
        <PanelsTopLeft size={18} aria-hidden="true" />
      </button>
      {open ? (
        <div id={panelId} className={styles.viewPanel}>
          <div role="group" aria-labelledby={`${panelId}-avatar`}>
            <p id={`${panelId}-avatar`} className={styles.viewSection}>
              Avatar
            </p>
            {TUTOR_VIEWS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={styles.viewOption}
                aria-pressed={view === option.value}
                onClick={() => {
                  onViewChange(option.value);
                  setOpen(false);
                }}
              >
                <span className={styles.viewIcon} aria-hidden="true">
                  {view === option.value ? <Check size={16} /> : null}
                </span>
                <span className={styles.viewLabel}>{option.label}</span>
              </button>
            ))}
            <button
              type="button"
              className={styles.viewOption}
              disabled={!fullscreenAvailable}
              onClick={() => {
                setOpen(false);
                onFullscreen(buttonRef.current);
              }}
            >
              <span className={styles.viewIcon} aria-hidden="true">
                <Maximize size={16} />
              </span>
              <span>
                <span className={styles.viewLabel}>Fullscreen</span>
                {fullscreenAvailable ? null : (
                  <span className={styles.viewDescription}>Video unavailable</span>
                )}
              </span>
            </button>
          </div>
          {conversation ? (
            <div
              role="group"
              aria-labelledby={`${panelId}-conversation`}
              className={styles.viewGroup}
            >
              <p id={`${panelId}-conversation`} className={styles.viewSection}>
                Conversation
              </p>
              {CONVERSATION_OPTIONS.map((option) => (
                <button
                  key={option.label}
                  type="button"
                  className={styles.viewOption}
                  aria-pressed={conversation.latestOnly === option.latestOnly}
                  onClick={() => {
                    conversation.onChange(option.latestOnly);
                    setOpen(false);
                  }}
                >
                  <span className={styles.viewIcon} aria-hidden="true">
                    {conversation.latestOnly === option.latestOnly ? <Check size={16} /> : null}
                  </span>
                  <span className={styles.viewLabel}>{option.label}</span>
                </button>
              ))}
            </div>
          ) : null}
          {translations ? (
            <div
              role="group"
              aria-labelledby={`${panelId}-translations`}
              className={styles.viewGroup}
            >
              <p id={`${panelId}-translations`} className={styles.viewSection}>
                Translations
              </p>
              <button
                type="button"
                role="switch"
                aria-checked={translations.enabled}
                className={styles.viewOption}
                onClick={() => translations.onEnabledChange(!translations.enabled)}
              >
                <span className={styles.viewIcon} aria-hidden="true">
                  {translations.enabled ? <Check size={16} /> : null}
                </span>
                <span className={styles.viewLabel}>Show translations</span>
              </button>
              {translations.enabled && translations.languageOptions.length > 1 ? (
                <label className={styles.viewSelectRow}>
                  <span className={styles.viewIcon} aria-hidden="true" />
                  <select
                    aria-label="Translation language"
                    value={translations.languageCode}
                    onChange={(event) => translations.onLanguageChange(event.target.value)}
                    className={styles.viewSelect}
                  >
                    {translations.languageOptions.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Camera: avatar shown or hidden while the voice conversation continues. */
export function TutorCameraButton({
  on,
  available,
  onToggle,
}: {
  on: boolean;
  /** False when the avatar video failed. */
  available: boolean;
  onToggle: () => void;
}) {
  const label = !available ? "Avatar unavailable" : on ? "Hide avatar" : "Show avatar";
  return (
    <button
      type="button"
      className={styles.stageIconButton}
      aria-pressed={on && available}
      aria-label={label}
      title={label}
      disabled={!available}
      onClick={onToggle}
    >
      {on && available ? (
        <Video size={18} aria-hidden="true" />
      ) : (
        <VideoOff size={18} aria-hidden="true" />
      )}
    </button>
  );
}

/**
 * Compact and Large: one header row. The tutor's identity and the microphone on
 * the left; the session's language, level and time, Live and end on the right.
 * Today's separate metadata row is folded in, so the header is shorter.
 */
export function TutorStageHeader({
  room,
  isActiveSession,
  isMuted,
  onToggleMute,
  onEndSession,
  assistantName,
  languageName,
  levelLabel,
  elapsed,
  mediaControls,
  speedControl,
  headerRef,
}: {
  room: Room;
  isActiveSession: boolean;
  isMuted?: boolean;
  onToggleMute?: () => void;
  onEndSession?: () => void;
  assistantName: string;
  languageName: string | null;
  levelLabel: string | null;
  elapsed: string;
  /** Camera and view controls, kept beside the microphone. */
  mediaControls: ReactNode;
  speedControl?: ReactNode;
  headerRef?: RefObject<HTMLDivElement | null>;
}) {
  const detail = [languageName, levelLabel].filter(Boolean).join(" · ");
  return (
    <div ref={headerRef} className={styles.stageHeader} data-tutor-stage-header>
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 select-none items-center justify-center overflow-hidden rounded-md border border-white/10 bg-white/[0.04]">
          <BrandMark size={36} className="h-full w-full object-contain p-0.5" />
        </div>
        <span className="truncate text-lg font-semibold">{assistantName}</span>
        {isActiveSession && onToggleMute ? (
          <SessionMicButton
            room={room}
            isActiveSession={isActiveSession}
            isMuted={isMuted}
            onToggleMute={onToggleMute}
          />
        ) : null}
        {mediaControls}
      </div>
      <div className={`flex min-w-0 items-center gap-3 ${styles.stageMeta}`}>
        {speedControl}
        <span className="whitespace-nowrap tabular-nums">
          {detail ? <span className={styles.statusDetail}>{detail} · </span> : null}
          {elapsed}
        </span>
        <span data-session-live className="flex items-center">
          <span
            className="relative inline-flex h-1.5 w-1.5 rounded-full bg-orange-300"
            aria-hidden="true"
          />
          <span className="sr-only">{sessionCopy.liveBadge}</span>
        </span>
        {onEndSession ? (
          <button
            type="button"
            data-session-end
            onClick={onEndSession}
            className="flex items-center justify-center rounded-md border border-white/10 text-white/55 transition-colors duration-200 hover:bg-white/[0.06] hover:text-white"
            aria-label="End Session"
            title="End Session (Esc)"
          >
            <X size={17} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
